import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { AuthContext } from '../contexts/AuthContext';
import api, { apiErrorMessage } from '../services/api';
import { categories } from '../utils/staticData';
import FloatingInput from '../components/ui/FloatingInput';
import SegmentedControl from '../components/ui/SegmentedControl';

function EditEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    secondaryLocation: '',
    secondaryAddress: '',
    secondaryTime: '',
    secondaryDescription: '',
    startTime: '',
    endTime: '',
    maxAttendees: '',
    categoryId: '',
    imageUrl: '',
    privacy: 'public',
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchEvent = async () => {
      try {
        const response = await api.get(`/api/events/${id}`);
        // Responses come wrapped as {success, message, data}. Reading the
        // envelope directly left hostId undefined, so the ownership check below
        // rejected every user -- including the actual host.
        const body = response.data;
        const event = body && body.data ? body.data : body;

        const hostId = event.HostID || event.host_id;
        if (currentUser.id !== hostId) {
          setError('You are not authorized to edit this event');
          setLoading(false);
          return;
        }

        const formatDateTime = (dateStr) => {
          if (!dateStr) return '';
          const date = new Date(dateStr);
          return date.toISOString().slice(0, 16);
        };

        setFormData({
          title: event.Title || event.title || '',
          description: event.Description || event.description || '',
          location: event.Location || event.location || '',
          address: event.Address || event.address || '',
          city: event.City || event.city || '',
          state: event.State || event.state || '',
          zipCode: event.ZipCode || event.zip_code || '',
          secondaryLocation: event.SecondaryLocation || event.secondary_location || '',
          secondaryAddress: event.SecondaryAddress || event.secondary_address || '',
          secondaryTime: formatDateTime(event.SecondaryTime || event.secondary_time),
          secondaryDescription: event.SecondaryDescription || event.secondary_description || '',
          startTime: formatDateTime(event.StartTime || event.start_time),
          endTime: formatDateTime(event.EndTime || event.end_time),
          maxAttendees: event.MaxAttendees || event.max_attendees || '',
          categoryId: event.CategoryID || event.category_id || '',
          imageUrl: event.ImageURL || event.image_url || '',
          privacy: event.Privacy || event.privacy || 'public',
        });

        setLoading(false);
      } catch (err) {
        console.error('Failed to load event data:', err);
        setError('Failed to load event data');
        toast.error('Failed to load event data');
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, currentUser, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (!formData.title?.trim()) {
        setError('Event title is required');
        toast.error('Event title is required');
        setSubmitting(false);
        return;
      }

      if (!formData.description?.trim()) {
        setError('Event description is required');
        toast.error('Event description is required');
        setSubmitting(false);
        return;
      }

      if (new Date(formData.startTime) >= new Date(formData.endTime)) {
        setError('End time must be after start time');
        toast.error('End time must be after start time');
        setSubmitting(false);
        return;
      }

      const eventData = {
        title: formData.title,
        description: formData.description,
        start_time: new Date(formData.startTime).toISOString(),
        end_time: new Date(formData.endTime).toISOString(),
        location: formData.location,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        secondary_location: formData.secondaryLocation,
        secondary_address: formData.secondaryAddress,
        secondary_time: formData.secondaryTime
          ? new Date(formData.secondaryTime).toISOString()
          : null,
        secondary_description: formData.secondaryDescription,
        max_attendees: formData.maxAttendees ? parseInt(formData.maxAttendees, 10) || null : null,
        category_id: formData.categoryId ? parseInt(formData.categoryId, 10) || null : null,
        image_url: formData.imageUrl,
        privacy: formData.privacy,
      };

      await api.put(`/api/events/${id}`, eventData);
      toast.success('Event updated successfully!');
      setSubmitting(false);
      setTimeout(() => {
        navigate(`/events/${id}`);
      }, 800);
    } catch (err) {
      console.error('Failed to update event:', err);
      const msg = apiErrorMessage(err, 'Failed to update event. Please try again.');
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  /* ----- Loading state ----- */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-surface-200 border-t-primary-500" />
        <p className="text-body-sm text-surface-400">Loading event data...</p>
      </div>
    );
  }

  /* ----- Fatal error (no form data) ----- */
  if (error && !formData.title) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-error-50 rounded-2xl border border-error-500/20 p-6 text-center">
          <svg className="w-10 h-10 text-error-400 mx-auto mb-3" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <p className="text-body font-medium text-error-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 bg-white text-surface-700 text-body-sm font-semibold rounded-xl border border-surface-200 hover:bg-surface-50 transition-colors duration-200"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-body-sm text-surface-500 hover:text-surface-700 mb-4 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Back
          </button>
          <h1 className="text-h1 text-surface-900">Edit Event</h1>
          <p className="text-body text-surface-500 mt-1">Update the details of your event</p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-error-50 border border-error-500/20 rounded-xl p-4 mb-6 flex items-start gap-3" role="alert">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-body-sm text-error-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section: Basic Info */}
          <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
            <div>
              <h2 className="text-h3 text-surface-900 mb-0.5">Basic Information</h2>
              <p className="text-body-sm text-surface-400">Give your event a name and description</p>
            </div>

            <FloatingInput
              label="Event Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />

            <div className="relative">
              <textarea
                id="description"
                name="description"
                rows={5}
                value={formData.description}
                onChange={handleChange}
                required
                placeholder=" "
                className="peer w-full bg-surface-50 border border-surface-200 rounded-xl text-surface-900 pt-5 pb-2 px-4 focus:ring-2 focus:bg-white focus:outline-none focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-body placeholder-transparent resize-none"
              />
              <label
                htmlFor="description"
                className="absolute left-4 transition-all duration-200 pointer-events-none select-none peer-placeholder-shown:top-4 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400 peer-focus:top-2 peer-focus:text-caption peer-focus:text-primary-600 top-2 text-caption text-surface-500"
              >
                Description <span className="text-error-500">*</span>
              </label>
            </div>

            <div>
              <label htmlFor="categoryId" className="block text-body-sm font-semibold text-surface-700 mb-2">
                Category
              </label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-body text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <FloatingInput
              label="Image URL"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
            />
          </section>

          {/* Section: Date & Time */}
          <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
            <div>
              <h2 className="text-h3 text-surface-900 mb-0.5">Date & Time</h2>
              <p className="text-body-sm text-surface-400">When does your event take place?</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="startTime" className="block text-body-sm font-semibold text-surface-700 mb-2">
                  Start Time <span className="text-error-500">*</span>
                </label>
                <input
                  id="startTime"
                  type="datetime-local"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                  className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-body text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label htmlFor="endTime" className="block text-body-sm font-semibold text-surface-700 mb-2">
                  End Time <span className="text-error-500">*</span>
                </label>
                <input
                  id="endTime"
                  type="datetime-local"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                  className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-body text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                />
              </div>
            </div>
          </section>

          {/* Section: Location */}
          <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
            <div>
              <h2 className="text-h3 text-surface-900 mb-0.5">Location</h2>
              <p className="text-body-sm text-surface-400">Where will your event be held?</p>
            </div>

            <FloatingInput label="Location Name" name="location" value={formData.location} onChange={handleChange} required />
            <FloatingInput label="Street Address" name="address" value={formData.address} onChange={handleChange} required />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FloatingInput label="City" name="city" value={formData.city} onChange={handleChange} required />
              <FloatingInput label="State" name="state" value={formData.state} onChange={handleChange} required />
              <FloatingInput label="Zip Code" name="zipCode" value={formData.zipCode} onChange={handleChange} required />
            </div>
          </section>

          {/* Section: Secondary Meetup */}
          <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
            <div>
              <h2 className="text-h3 text-surface-900 mb-0.5">Secondary Meetup</h2>
              <p className="text-body-sm text-surface-400">Optional: A pre/post event gathering spot</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FloatingInput label="Secondary Location Name" name="secondaryLocation" value={formData.secondaryLocation} onChange={handleChange} />
              <div>
                <label htmlFor="secondaryTime" className="block text-body-sm font-semibold text-surface-700 mb-2">
                  Secondary Meetup Time
                </label>
                <input
                  id="secondaryTime"
                  type="datetime-local"
                  name="secondaryTime"
                  value={formData.secondaryTime}
                  onChange={handleChange}
                  className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-body text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                />
              </div>
            </div>

            <FloatingInput label="Secondary Location Address" name="secondaryAddress" value={formData.secondaryAddress} onChange={handleChange} />

            <div className="relative">
              <textarea
                id="secondaryDescription"
                name="secondaryDescription"
                rows={3}
                value={formData.secondaryDescription}
                onChange={handleChange}
                placeholder=" "
                className="peer w-full bg-surface-50 border border-surface-200 rounded-xl text-surface-900 pt-5 pb-2 px-4 focus:ring-2 focus:bg-white focus:outline-none focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-body placeholder-transparent resize-none"
              />
              <label
                htmlFor="secondaryDescription"
                className="absolute left-4 transition-all duration-200 pointer-events-none select-none peer-placeholder-shown:top-4 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400 peer-focus:top-2 peer-focus:text-caption peer-focus:text-primary-600 top-2 text-caption text-surface-500"
              >
                Secondary Meetup Description
              </label>
            </div>
          </section>

          {/* Section: Details */}
          <section className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8 space-y-5">
            <div>
              <h2 className="text-h3 text-surface-900 mb-0.5">Event Details</h2>
              <p className="text-body-sm text-surface-400">Additional settings for your event</p>
            </div>

            <FloatingInput
              label="Maximum Attendees"
              name="maxAttendees"
              type="number"
              value={formData.maxAttendees}
              onChange={handleChange}
              min="1"
            />

            <SegmentedControl
              label="Privacy"
              name="privacy"
              value={formData.privacy}
              onChange={(val) => setFormData((prev) => ({ ...prev, privacy: val }))}
              options={[
                {
                  value: 'public',
                  label: 'Public',
                  icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 3.03v.568c0 .334.148.65.405.864a11.944 11.944 0 005.725 2.795c.21.047.42-.012.577-.149l.408-.35a.75.75 0 011.13.128A11.96 11.96 0 0121.75 12c0 6.627-5.373 12-12 12S-2.25 18.627-2.25 12 3.123 0 9.75 0c1.86 0 3.625.426 5.2 1.186" />
                    </svg>
                  ),
                },
                {
                  value: 'private',
                  label: 'Private',
                  icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  ),
                },
              ]}
            />
          </section>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pb-8">
            <button
              type="button"
              onClick={() => navigate(`/events/${id}`)}
              className="px-6 py-2.5 text-body-sm font-semibold text-surface-600 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-surface-300/40 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Updating...
                </span>
              ) : (
                'Update Event'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default EditEvent;
